import {
  inject,
  Injectable,
  InjectionToken,
  OnDestroy,
  Signal,
} from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  concatMap,
  distinctUntilChanged,
  filter,
  forkJoin,
  from,
  isObservable,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  Subscription,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';

import type { Maybe } from '@plugins/shared/types';
import { returnAsArray } from '@plugins/shared/utils/arrayUtils/array-utils';
import { isNonNullish, isNullish } from '@plugins/shared/utils/assertFunctions';
import { flatten } from '@plugins/shared/utils/objectUtils/flatten';
import { omitProperties } from '@plugins/shared/utils/objectUtils/omitProperties';

import { interpolate } from '../utils/interpolate';

import {
  BrowserLanguageService,
  BrowserLanguageToken,
  DirectBrowserLanguageService,
} from './browser-language.service';
import {
  MockTranslateExternalOverrideService,
  TranslateExternalOverrideService,
  TranslateExternalOverrideToken,
} from './translate-override.service';
import type {
  AddTranslationLoaderArgs,
  Dependency,
  DependencyRegister,
  DependencyMap,
  IncomingTranslation,
  Language,
  SetLanguageTranslationsArgs,
  Translation,
  TranslationParams,
  TranslationType,
  Loader,
  SetDependencyTranslationsArgs,
  DependencyRegisterValue,
} from './types';

export type TranslateConfig = {
  /** The languages that will be loaded when `loadRegisteredLanguageTranslations` method is invoked.
   *  Defaults to [`en`, `de`]. */
  languages: Language[];
  /** The default language that will be used if the currently requested
   *  language is not available. Defaults to `en`. */
  fallbackLanguage: string;
  /** If the currently available language does not have the speficied key,
   * should a translation from the fallback language be used?
   * Defaults to `true`. */
  useFallbackLanguageOnMissing: boolean;

  /** If set to true, no attempt wil be made to
   * query the middleware store for translation overrides.
   * Browser language will also be fetched directly from navigator.
   * Defaults to `false`. */
  sdkDisabled: boolean;
};
export type TranslateConfigToken = Partial<TranslateConfig>;

export const configDefaults: TranslateConfig = {
  fallbackLanguage: 'en',
  useFallbackLanguageOnMissing: true,
  sdkDisabled: false,
  languages: ['en', 'de'],
};
export const parseConfig = (
  config?: Maybe<TranslateConfigToken>,
): TranslateConfig => {
  if (!config) return configDefaults;
  return Object.fromEntries(
    Object.entries(configDefaults).map(([key, value]) => {
      return [key, config[key as keyof TranslateConfig] ?? value];
    }),
  ) as TranslateConfig;
};

export const TRANSLATE_CONFIG = new InjectionToken<TranslateConfigToken>(
  'translateService.config',
);

@Injectable({ providedIn: 'root' })
export abstract class AbstractTranslateService implements OnDestroy {
  /** Static service accessor, for use outside of injection context, usually with decorators
   * that are constructed with the `registerTranslationSource` helper. */
  static service: BehaviorSubject<AbstractTranslateService | null>;

  constructor(
    // eslint-disable-next-line @angular-eslint/prefer-inject
    protected middlewareOverrideService: TranslateExternalOverrideToken,
    // eslint-disable-next-line @angular-eslint/prefer-inject
    protected browserLanguageService: BrowserLanguageToken,
    // eslint-disable-next-line @angular-eslint/prefer-inject
    readonly config = configDefaults,
  ) {
    if (!this.config.sdkDisabled) {
      this.addTranslationLoader({
        key: this.externalDependencyKey,
        loader: this.externalDependencyLoader,
        type: 'set',
      });
    }
    this.browserLanguageService.locale$
      .pipe(
        // Only keep the language information
        map((lang) => lang.split(/(-|_)/)[0]),
        tap((lang) => {
          this._language.next(lang);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
    this.languageLoaded$
      .pipe(
        tap((isLoaded) => {
          this._isLoaded.next(isLoaded);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
    this.middlewareOverrideService.overrides$
      .pipe(
        tap((overrides) => {
          Object.keys(overrides).forEach((language) => {
            this.resolvedKeyValuesCache.get(language)?.clear();
            this.setDependencyAsLoaded(this.externalDependencyKey, language);
          });
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  protected externalDependencyKey = 'middleware-override';
  protected externalDependencyLoader = (lang: string) =>
    this.middlewareOverrideService.getOverride(lang);
  protected defaultFallbackLanguage = 'en';
  protected registeredLanguages = [];
  protected destroy$ = new Subject<void>();
  protected registeredDependencies = new BehaviorSubject<DependencyRegister>(
    new Map(),
  );
  protected loadedDependencies = new BehaviorSubject<DependencyMap>(new Map());
  protected readonly _language = new BehaviorSubject<Language>(
    this.config.fallbackLanguage,
  );
  protected readonly _translations = new BehaviorSubject<TranslationType>({});

  protected readonly _isLoaded = new BehaviorSubject(false);
  protected readonly loadersSubscriptions = new Map<Dependency, Subscription>();
  readonly translations$ = this._translations.asObservable();
  readonly language$ = this._language.asObservable();

  /** When a raw translate value is resolved from available translations for a language,
   * it is cached here. The raw translate value can then be subject to variable interpolation, if needed.  */
  protected readonly resolvedKeyValuesCache = new Map<
    Language,
    Map<string, string>
  >();

  /** Indicate that the current language has been successfully loaded
   * after all of the registered dependencies have been resolved. */
  readonly languageLoaded$ = combineLatest({
    language: this.language$,
    dependencies: this.registeredDependencies,
    loaded: this.loadedDependencies,
  }).pipe(
    map(({ language, dependencies, loaded }) => {
      const targetLoaded = loaded.get(language);
      if (dependencies.size === 0 || targetLoaded === undefined) {
        return false;
      }
      // The override dependency by itself is not a complete translation and
      // is not sufficient for the language to be recgonized as loaded.
      if (
        dependencies.size === 1 &&
        dependencies.has(this.externalDependencyKey)
      ) {
        return false;
      }
      return [...dependencies].every((dep) => targetLoaded.has(dep[0]));
    }),
    distinctUntilChanged(),
    shareReplay(1),
  );

  get fallbackLanguage(): string {
    return this.config.fallbackLanguage;
  }
  get currentLanguage(): string {
    return this._language.getValue();
  }
  get translations(): TranslationType {
    return this._translations.getValue();
  }
  get currentTranslations(): Translation | undefined {
    return this.getTranslations();
  }

  get externalOverrides(): Signal<TranslationType> {
    return this.middlewareOverrideService.flattenOverrides;
  }

  get languageLoaded() {
    return this._isLoaded.getValue();
  }

  get dependencies(): DependencyRegister {
    return this.registeredDependencies.getValue();
  }

  getPlaceholder(key: string): string {
    return `<i18n:${key}>`;
  }

  protected get currentLangCache() {
    return this.resolvedKeyValuesCache.get(this._language.getValue());
  }

  protected getResolvedCacheValue(key: string) {
    return this.currentLangCache?.get(key);
  }

  protected setResolvedCacheValue(key: string, value: string) {
    const current = this.currentLangCache;
    if (!current) {
      this.resolvedKeyValuesCache.set(this._language.getValue(), new Map());
      this.setResolvedCacheValue(key, value);
      return;
    }
    current.set(key, value);
  }

  /** Return translation key from the current state of loaded translations.
   * Does not check if translations have been loaded.
   *
   * Keys are matched against translations in the following way:
   * - External overrides
   * - Current language
   * - Fallback language, if enabled and present
   * - Placeholder */
  instant<Key extends string>(
    key: string,
    params?: Maybe<TranslationParams<Key>>,
  ): string {
    const cacheValue = this.getResolvedCacheValue(key);
    if (cacheValue) {
      return interpolate(cacheValue, params);
    }
    const rawValue = this.getOverrideOrTranslation(key);
    this.setResolvedCacheValue(key, rawValue);
    return interpolate(rawValue, params);
  }

  /** Stream translation for provided key on language changes. Verifies that languages are loaded. */
  get<Key extends string>(
    key: string,
    params?: Maybe<TranslationParams<Key>>,
  ): Observable<string> {
    return this.language$.pipe(
      switchMap(() => this.languageLoaded$.pipe(filter((loaded) => loaded))),
      map(() => this.instant(key, params)),
    );
  }

  /** Provide translation for key after language is loaded as an single-emission observable. */
  getOnce<Key extends string>(
    key: string,
    params?: Maybe<TranslationParams<Key>>,
  ): Observable<string> {
    return this.get(key, params).pipe(take(1));
  }

  setLanguage(lang: string) {
    this._language.next(lang);
  }

  /** Set new language, then return updated translation set once language is loaded. */
  use(
    lang: string,
    /** Should fallback translations be provided in case
     * the target language translations are missing?
     *
     * Defaults to current config settings. */
    provideFallbackIfMissing = this.config.useFallbackLanguageOnMissing,
  ) {
    this.setLanguage(lang);
    return this.languageLoaded$.pipe(
      startWith(this.languageLoaded),
      filter((loaded) => loaded),
      map(() => {
        const translations = this.getTranslations(lang);
        if (!isNullish(translations)) return translations;
        if (provideFallbackIfMissing) {
          return this.getTranslations(this.config.fallbackLanguage);
        }
        return translations;
      }),
      take(1),
    );
  }

  /** Returns all translations for provided languages.
   * Loads any language that has not yet been loaded for currently registered dependencies. */
  loadRegisteredLanguageTranslations() {
    const allTranslations = this._translations.getValue();
    return forkJoin(
      this.config.languages.map((language) => {
        const languageTranslations = allTranslations[language];
        if (languageTranslations) {
          return of(languageTranslations);
        }
        const translations = [...this.dependencies.entries()].map(
          ([key, { loader, type }]) =>
            this.getTranslationsFromLoader(loader, language).pipe(
              tap((translations) => {
                if (key !== this.externalDependencyKey) {
                  this.setDependencyTranslations({
                    translations,
                    language,
                    key,
                    type,
                  });
                }
              }),
            ),
        );
        return forkJoin(translations);
      }),
    ).pipe(
      map(() => {
        const translations = this._translations.getValue();
        return this.config.languages.reduce<TranslationType>((acc, curr) => {
          acc[curr] = translations[curr];
          return acc;
        }, {});
      }),
    );
  }

  /** Returns all translations for provided language.
   * If no language code is provided, returns translations for current language. */
  getTranslations(lang?: string): Translation | undefined {
    const allTranslations = this._translations.getValue(),
      language = lang ?? this._language.getValue();
    return allTranslations[language];
  }

  /** Clear translations for the target language (if not supplied defaults to current)
   * and remove caching and loaded dependencies entries. */
  clearTranslations(language = this.currentLanguage): void {
    const repo = this._translations.getValue();
    this._translations.next(omitProperties(repo, language));
    [...this.dependencies].forEach((dep) => {
      this.removeDependencyAsLoaded(dep[0], language);
    });
    this.resolvedKeyValuesCache.get(language)?.clear();
  }

  /** Set translations for the target language.
   * All previous translations for the language will be cleared.
   * All registered dependencies will be marked as loaded.
   *
   * External overrides are left intact. */
  setTranslations(
    translations: IncomingTranslation,
    language = this.currentLanguage,
  ): void {
    this.updateTranslationsForLanguage({
      incoming: translations,
      language,
      type: 'set',
    });
    [...this.dependencies].forEach((dep) => {
      this.setDependencyAsLoaded(dep[0], language);
    });
  }

  /** Update existing translations for target key.
   *
   * Translation entries with same keys will be overwritten.
   * Non-matching entries will be left as they were.
   *
   * Usually called from plugins.  */
  updateKeyTranslations(
    translations: IncomingTranslation,
    key: string,
    language = this.currentLanguage,
  ) {
    this.updateKeyedTranslation({
      incoming: translations,
      type: 'update',
      key,
      language,
    });
  }

  /** Non-overwriting merge for target key. if translation property already exists it will be preserved.
   * Usually called from libraries whose translations are subordinate to app-level overrides. */
  supplementKeyTranslations(
    translations: IncomingTranslation,
    key: string,
    language = this.currentLanguage,
  ) {
    this.updateKeyedTranslation({
      incoming: translations,
      type: 'supplement',
      key,
      language,
    });
  }

  registerDependency(key: string, args: DependencyRegisterValue) {
    const current = this.dependencies;
    if (current.has(key)) return;
    current.set(key, args);
    this.registeredDependencies.next(current);
  }

  addTranslationLoader({ loader, key, type }: AddTranslationLoaderArgs) {
    if (this.loadersSubscriptions.get(key)) {
      return;
    }
    this.registerDependency(key, { loader, type });
    const subscription = this.language$
      .pipe(
        startWith(
          this.config.useFallbackLanguageOnMissing
            ? this.fallbackLanguage
            : this.currentLanguage,
        ),
        filter(() => !this.loadedDependencies.getValue().has(key)),
        concatMap((language) =>
          this.getTranslationsFromLoader(loader, language).pipe(
            map((translations) => ({ translations, language })),
          ),
        ),
        tap(({ translations, language }) => {
          if (key !== this.externalDependencyKey) {
            this.setDependencyTranslations({
              translations,
              language,
              key,
              type,
            });
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
    this.loadersSubscriptions.set(key, subscription);
  }

  protected getTranslationsFromLoader(loader: Loader, language: Language) {
    const sources = returnAsArray(loader(language));
    const observableSources = sources.map((imp) => {
      return from(isObservable(imp) ? imp : Promise.resolve(imp)).pipe(
        catchError(() => of(undefined)),
      );
    });
    return forkJoin(observableSources).pipe(
      map((sources) => {
        return sources
          .filter((source): source is IncomingTranslation =>
            isNonNullish(source),
          )
          .map((source) => {
            if ('__esModule' in source && 'default' in source) {
              return source['default'] as IncomingTranslation;
            }
            return source;
          });
      }),
    );
  }

  protected setDependencyTranslations({
    translations,
    key,
    language,
    type,
  }: SetDependencyTranslationsArgs) {
    /** If translations are missing, then the dependency loading
     * is satisfied for the current language. Handling of the translation value
     * and using fallback language is done elsewhere. */
    if (!translations.length) {
      this.setDependencyAsLoaded(key, language);
      return;
    }
    const merged = returnAsArray<Record<string, unknown>>(translations).reduce(
      (acc, prev) => {
        acc = { ...acc, ...prev };
        return acc;
      },
      {},
    );
    this.updateKeyedTranslation({ incoming: merged, type, key, language });
  }

  /** For target translation key and type, update the translations
   * and set the dependency key as loaded.*/
  protected updateKeyedTranslation({
    incoming,
    key,
    type,
    language,
  }: SetLanguageTranslationsArgs) {
    this.updateTranslationsForLanguage({ incoming, type, language });
    this.setDependencyAsLoaded(key, language);
  }

  /** Update target translations in accordance with the current update
   * type and clear any cached translation values.
   *
   * Has no concept of translation dependencies - this should be managed
   * by the caller.*/
  protected updateTranslationsForLanguage({
    incoming,
    type,
    language,
  }: Omit<SetLanguageTranslationsArgs, 'key'>) {
    const translations = this._translations.getValue();
    const currentVal = translations[language];
    const translationValues = flatten<string>(incoming);
    let newVal = currentVal;
    switch (type) {
      case 'set':
        newVal = translationValues;
        break;
      case 'supplement':
        newVal = { ...translationValues, ...currentVal };
        break;
      case 'update':
        newVal = { ...currentVal, ...translationValues };
        break;
      default:
        throw new Error(
          `Incorrect language translation type set. Expected set, update, or supplement, got ${type as string}.`,
        );
    }
    this._translations.next({ ...translations, [language]: newVal });
    this.resolvedKeyValuesCache.get(language)?.clear();
  }

  protected setDependencyAsLoaded(key: string, language: string) {
    const dependencyMap = this.loadedDependencies.getValue();

    const langSet = dependencyMap.get(language) ?? new Set();
    dependencyMap.set(language, langSet.add(key));
    this.loadedDependencies.next(dependencyMap);
  }

  protected removeDependencyAsLoaded(key: string, language: string): void {
    const dependencyMap = this.loadedDependencies.getValue();
    const langSet = dependencyMap.get(language);
    if (!langSet) return;
    langSet.delete(key);
    this.loadedDependencies.next(dependencyMap);
  }

  protected getOverrideOrTranslation(key: string) {
    const language = this._language.getValue();
    const getForLang = (lang: string) => {
      return (
        this.externalOverrides()[lang]?.[key] ??
        this.getTranslations(lang)?.[key]
      );
    };
    let rawValue = getForLang(language);
    if (rawValue) return rawValue;
    if (this.config.useFallbackLanguageOnMissing) {
      rawValue = getForLang(this.config.fallbackLanguage);
    }
    return rawValue ?? this.getPlaceholder(key);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

/**
 * Translations management service.
 *
 * To supply translations, use:
 * - translation loader decorators or custom translation modules on target components / modules
 *  (preferred, see [documentation](../loaders/translation-loaders.ts))
 * - manual loader registration (see [Loader](../types.ts) type description)
 * - manual setting of translations with translation service methods
 *
 * To use translations: Use either the `TranslatePipe` or `instant`, `get`, `getOnce` methods among others. */
@Injectable({ providedIn: 'root' })
export class TranslateService extends AbstractTranslateService {
  static override service = new BehaviorSubject<TranslateService | null>(null);

  constructor() {
    const config = parseConfig(inject(TRANSLATE_CONFIG, { optional: true }));
    // If we are disabling external overrides our application might not be
    // running in a platform context that would allow access to the SDK
    // which is why we would inject the `DirectBrowserLanguageService` and
    // the `MockTranslateExternalOverrideService`.
    const browserLanguageService = config.sdkDisabled
      ? inject(DirectBrowserLanguageService)
      : inject(BrowserLanguageService);

    const middlewareOverride = config.sdkDisabled
      ? new MockTranslateExternalOverrideService()
      : inject(TranslateExternalOverrideService);
    super(middlewareOverride, browserLanguageService, config);
    TranslateService.service.next(this);
  }
}
